import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// OATH SERVICE — TB-elimination pledge capture + SuperAdmin analytics.
//
// Public (no auth required):
//   POST /oaths            — submit the pledge (idempotent on email/mobile).
//   GET  /oaths/exists     — has a pledge already been taken for this email or mobile?
//
// Admin (Bearer token, ADMIN/SUPER_ADMIN):
//   GET /oaths/stats       — aggregate analytics (totals, by-state, trend, top districts).
//   GET /oaths/list        — searchable, paginated list of pledge-takers.
//   GET /oaths/export      — CSV download of the filtered list.
// ================================================================================

export interface OathPayload {
  firstName: string
  middleName?: string
  surname: string
  email: string
  mobile: string
  state: string
  district: string
  address?: string
  /** Pledge language the visitor selected (en/hi/mr). */
  language: string
  accepted: boolean
}

export interface OathRecord {
  id: number
  firstName: string
  middleName?: string
  surname: string
  fullName: string
  email: string
  mobile: string
  state: string
  district: string
  address?: string
  /** Pledge language code (en/hi/mr); null for legacy rows. */
  language?: string | null
  createdAt: string
}

export interface OathSubmitResult {
  alreadyTaken: boolean
  oath: OathRecord
}

export interface OathDayBreakdown {
  date: string
  count: number
}

export interface OathDistrictBreakdown {
  district: string
  count: number
}

export interface OathStats {
  totalOaths: number
  today: number
  thisWeek: number
  uniqueEmails: number
  topState: string | null
  byState: Record<string, number>
  byLanguage?: Record<string, number>
  topDistricts: OathDistrictBreakdown[]
  trend: OathDayBreakdown[]
}

export interface OathSearchParams {
  search?: string
  state?: string
  district?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  size?: number
  sortBy?: string
}

const unwrap = <T,>(p: Promise<{ data: ApiResponse<T> }>) => p.then((r) => r.data.data)

/** POST /oaths — submit the pledge (returns alreadyTaken=true for a returning visitor). */
export async function submitOath(payload: OathPayload): Promise<OathSubmitResult> {
  return unwrap<OathSubmitResult>(api.post('/oaths', payload))
}

/** GET /oaths/exists — has the pledge already been taken for this email or mobile? */
export async function checkOathExists(params: { email?: string; mobile?: string }): Promise<boolean> {
  return unwrap<boolean>(api.get('/oaths/exists', { params }))
}

/** GET /oaths/stats — aggregate analytics (admin). */
export async function getOathStats(): Promise<OathStats> {
  return unwrap<OathStats>(api.get('/oaths/stats'))
}

/** GET /oaths/list — searchable, paginated list (admin). */
export async function searchOaths(params: OathSearchParams): Promise<PaginatedResponse<OathRecord>> {
  return unwrap<PaginatedResponse<OathRecord>>(api.get('/oaths/list', { params }))
}

/**
 * GET /oaths/export — download the filtered pledges as a CSV (admin). Requests
 * with `responseType: 'blob'` so Devanagari names in the file stay intact, then
 * triggers a browser download.
 */
export async function exportOaths(params: OathSearchParams): Promise<void> {
  const res = await api.get('/oaths/export', { params, responseType: 'blob' })
  const url = window.URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = 'oaths.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}